import { convert } from "@/libs/units";
import type { DataSet, NumericPoint } from "@incytestudios/ddv";
import { isFinitePoint, isNaturalPoint } from "../_common";
import { getSimplifiedProducedTdh } from "../tornado/tdh/getProducedDataSetsTdh";
import type { SinglePumpChartContext } from "..";
import { getDensityCorrectedProducedPoints } from "../_common/densityCorrection";

const getProducedTDHChartData = (context: SinglePumpChartContext) => {
  const {
    hypotheticals: { hypotheticalFlowRates },
    ascendingOrderPumps,
    gasHeadRerateRatio,
    pumpWearHeadCorrectionRatio,
    dischargeFluidDensityLBMFT3,
    frequencyRatio
  } = context;

  // Generate produced TDH points for each hypothetical flow rate
  // with the simplified produced tdh calculation
  const producedTDHData = hypotheticalFlowRates.map((flowRateBblD: number) => {
    const data = getSimplifiedProducedTdh(
      flowRateBblD,
      frequencyRatio,
      ascendingOrderPumps,
      gasHeadRerateRatio,
      pumpWearHeadCorrectionRatio,
      dischargeFluidDensityLBMFT3
    );

    return {
      x: flowRateBblD,
      tdhFt: data.tdh,
      tdhPressurePSIA: data.tdhPressure
    };
  });
  return {
    producedTDHData
  };
};

export const getDataSetTDH = (
  context: SinglePumpChartContext,
  tdhDensityCorrectionRatio: number = 1,
  pressureDensityCorrectionRatio: number = 1
): DataSet & {
  originalPoints: NumericPoint[];
  originalPointsForEfficiency: NumericPoint[];
} => {
  const {
    baseXUnit,
    targetXUnit,
    targetXUnitLabel,
    baseYUnit,
    targetYUnit,
    targetYUnitLabel,
    isScenarioEspShore
  } = context;
  const data = getProducedTDHChartData(context);
  // Eff calc needs the tdh values only regardless of what we're charting
  const originalPointsForEfficiency = data.producedTDHData.map<NumericPoint>(
    d => ({
      x: d.x,
      y: d.tdhFt
    })
  );
  // These are the same as originalPointsForEfficiency if ESP
  const originalProducedTDHPoints = data.producedTDHData.map<NumericPoint>(
    d => ({
      x: d.x,
      y: isScenarioEspShore ? d.tdhFt : d.tdhPressurePSIA
    })
  );

  const targetDensityCorrectionRatio = isScenarioEspShore
    ? tdhDensityCorrectionRatio
    : pressureDensityCorrectionRatio;
  // Apply density correction ratio to the original produced TDH points
  // densityCorrectionRatio is already either TDH or Pressure depending on scenario type,
  // so we can apply it directly to the y-values regardless of chart type
  const densityAdjustedProducedTDHPoints = getDensityCorrectedProducedPoints(
    originalProducedTDHPoints,
    targetDensityCorrectionRatio
  );

  const convertedProducedTDHPoints = densityAdjustedProducedTDHPoints
    .map<NumericPoint>(d => {
      return {
        x: convert(d.x, baseXUnit, targetXUnit),
        y: convert(d.y, baseYUnit, targetYUnit)
      };
    })
    .filter(isFinitePoint)
    .filter(isNaturalPoint);

  const title = isScenarioEspShore ? `Head Produced` : `Differential Pressure`;
  const yAxisLabel = isScenarioEspShore
    ? `Head Produced`
    : `Differential Pressure`;

  return {
    title,
    xAxisLabel: `In Situ Flow Rate (${targetXUnitLabel})`,
    yAxisLabel: `${yAxisLabel} (${targetYUnitLabel})`,
    points: convertedProducedTDHPoints,
    originalPoints: densityAdjustedProducedTDHPoints,
    originalPointsForEfficiency
  };
};
